import React, { Component } from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import {
    Line, Bar, Pie, Text, Gauge, Treemap, Radar, MultiText, LMX,
} from 'frontend-charts'
import i18n from 'i18next'
import Modal from '../../UniformComponents/Modal'
import Button from '../../UniformComponents/Button'
import ModalFooter from '../../UniformComponents/ModalFooter'
import ModalHeader from '../../UniformComponents/ModalHeader'
import Carousel from '../../UniformComponents/Carousel'
import Checkbox from '../form/Checkbox'
import Tooltip from '../../UniformComponents/Tooltip'
import TreeviewSelect from '../form/TreeviewSelect'
import {
    getIndustries,
    getProductTypes,
    getSurveyTypes,
} from '../../utils/helpers'

const chartPropResets = [
    {
        path: ['yAxis', 'axisLabel', 'show'],
        value: false,
    },
    {
        path: ['yAxis', 'splitLine', 'show'],
        value: false,
    },
    {
        path: ['xAxis', 'axisLabel', 'show'],
        value: false,
    },
    {
        path: ['xAxis', 'show'],
        value: false,
    },
    {
        path: ['tooltip', 'show'],
        value: false,
    },
    {
        path: ['grid'],
        value: {
            bottom: '15%', top: '15%', left: '15%', right: '15%',
        },
    },
]

const chartIcons = [
    {
        chart: <Bar
            chartData={[
                {
                    name: 'series A',
                    data: [
                        1,
                        2,
                        3,
                    ],
                },
            ]}
            benchmarkData={[
                {
                    name: 'series B',
                    data: [
                        3,
                        3,
                        1,
                    ],
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={chartPropResets}
        />,
        key: 'groupedColumn',
        defaultSize: 8,
    },
    {
        chart: <Bar
            chartData={[
                {
                    name: 'series A',
                    data: [
                        1,
                        2,
                        3,
                    ],
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={chartPropResets}
        />,
        key: 'column',
        defaultSize: 8,
    },
    {
        chart: <Bar
            xAxisLabels={['label one', 'label two', 'label three']}
            chartData={[
                {
                    name: 'series A',
                    data: [
                        1,
                        2,
                        3,
                    ],
                },
                {
                    name: 'series B',
                    data: [
                        3,
                        1,
                        1,
                    ],
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={chartPropResets}
        />,
        key: 'stackedColumn',
        defaultSize: 8,
    },
    {
        chart: <Bar
            chartData={[
                {
                    name: 'series A',
                    data: [
                        1,
                        2,
                        3,
                    ],
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={[
                {
                    path: ['xAxis', 'axisLabel', 'show'],
                    value: false,
                },
                {
                    path: ['xAxis', 'splitLine', 'show'],
                    value: false,
                },
                {
                    path: ['yAxis', 'axisLabel', 'show'],
                    value: false,
                },
                {
                    path: ['yAxis', 'show'],
                    value: false,
                },
                {
                    path: ['tooltip', 'show'],
                    value: false,
                },
            ]}
            transpose
        />,
        key: 'bar',
        defaultSize: 4,
    },
    {
        chart: <Bar
            xAxisLabels={['label one', 'label two', 'label three']}
            chartData={[
                {
                    name: 'series A',
                    data: [
                        1,
                        2,
                        3,
                    ],
                },
                {
                    name: 'series B',
                    data: [
                        3,
                        1,
                        1,
                    ],
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={[
                {
                    path: ['xAxis', 'axisLabel', 'show'],
                    value: false,
                },
                {
                    path: ['xAxis', 'splitLine', 'show'],
                    value: false,
                },
                {
                    path: ['yAxis', 'axisLabel', 'show'],
                    value: false,
                },
                {
                    path: ['yAxis', 'show'],
                    value: false,
                },
                {
                    path: ['tooltip', 'show'],
                    value: false,
                },
            ]}
            transpose
        />,
        key: 'stackedBar',
        defaultSize: 4,
    },
    {
        chart: <Pie
            chartData={[
                {
                    value: 20,
                    name: 'series 1',
                },
                {
                    value: 30,
                    name: 'series 2',
                },
                {
                    value: 10,
                    name: 'series 3',
                },
                {
                    value: 40,
                    name: 'series 4',
                },
                {
                    value: 30,
                    name: 'series 5',
                },
                {
                    value: 60,
                    name: 'series 6',
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={[
                {
                    path: ['legend', 'show'],
                    value: false,
                },
                {
                    path: ['tooltip', 'show'],
                    value: false,
                },
            ]}
            compact
        />,
        key: 'pie',
        defaultSize: 2,
    },
    {
        chart: <Pie
            chartData={[
                {
                    value: 20,
                    name: 'series 1',
                },
                {
                    value: 30,
                    name: 'series 2',
                },
                {
                    value: 10,
                    name: 'series 3',
                },
                {
                    value: 40,
                    name: 'series 4',
                },
                {
                    value: 30,
                    name: 'series 5',
                },
                {
                    value: 60,
                    name: 'series 6',
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={[
                {
                    path: ['legend', 'show'],
                    value: false,
                },
                {
                    path: ['tooltip', 'show'],
                    value: false,
                },
            ]}
            compact
            donut
        />,
        key: 'donut',
        defaultSize: 2,
    },
    {
        chart: <Treemap
            chartData={[
                {
                    value: 20,
                    name: 'series 1',
                },
                {
                    value: 30,
                    name: 'series 2',
                },
                {
                    value: 10,
                    name: 'series 3',
                },
                {
                    value: 40,
                    name: 'series 4',
                },
                {
                    value: 30,
                    name: 'series 5',
                },
                {
                    value: 60,
                    name: 'series 6',
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={[
                {
                    path: ['legend', 'show'],
                    value: false,
                },
                {
                    path: ['tooltip', 'show'],
                    value: false,
                },
            ]}
            compact
            donut
        />,
        key: 'treemap',
        defaultSize: 4,
    },
    {
        chart: <Radar
            chartData={[
                {
                    name: 'series A',
                    data: [
                        1,
                        2,
                        3,
                    ],
                },
            ]}
            indicators={[{ name: 'series A' }]}
            style={{ height: '60px', width: '60px' }}
        />,
        key: 'radar',
        defaultSize: 8,
    },
    {
        chart: <Text
            mainText="$1000.50"
            subText="Company net sales"
            style={{ height: '60px', width: '60px' }}
        />,
        key: 'text',
        defaultSize: 2,
    },
    {
        chart: <Line
            chartData={[
                {
                    name: 'series A',
                    data: [
                        0,
                        1,
                        2,
                        3,
                    ],
                },
            ]}
            benchmarkData={[
                {
                    name: 'series B',
                    data: [
                        1,
                        2,
                        3,
                        4,
                    ],
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={chartPropResets}
        />,
        key: 'line',
        defaultSize: 8,
    },
    {
        chart: <Line
            xAxisLabels={['label one', 'label two', 'label three']}
            chartData={[
                {
                    name: 'series A',
                    data: [
                        0,
                        1,
                        2,
                        3,
                    ],
                },
                {
                    name: 'series B',
                    data: [
                        1,
                        4,
                        3,
                        4,
                    ],
                },
            ]}
            style={{ height: '60px', width: '60px' }}
            layoutEdit={chartPropResets}
            type="stack"
        />,
        key: 'stackedLine',
        defaultSize: 8,
    },
    {
        chart: <Gauge
            chartData={45}
            style={{ height: '60px', width: '60px' }}
            containerSize={{ height: 190, width: 190 }}
            layoutEdit={[
                {
                    path: ['series', 0, 'pointer', 'width'],
                    value: 3,
                },
                {
                    path: ['series', 0, 'pointer', 'length'],
                    value: '52%',
                },
                {
                    path: ['series', 0, 'pointer', 'offsetCenter'],
                    value: [0, '-7%'],
                },
                {
                    path: ['series', 0, 'axisTick', 'distance'],
                    value: -15,
                },
                {
                    path: ['series', 0, 'detail', 'rich', 'a', 'fontSize'],
                    value: 25,
                },
                {
                    path: ['series', 0, 'detail', 'rich', 'a', 'lineHeight'],
                    value: 25,
                },
                {
                    path: ['series', 0, 'detail', 'show'],
                    value: false,
                },
                {
                    path: ['series', 0, 'axisLabel', 'show'],
                    value: false,
                },
                {
                    path: ['series', 0, 'splitLine', 'show'],
                    value: false,
                },
                {
                    path: ['series', 0, 'startAngle'],
                    value: 180,
                },
                {
                    path: ['series', 0, 'endAngle'],
                    value: 0,
                },

            ]}
        />,
        key: 'gauge',
        defaultSize: 2,
    },
    {
        chart: <Gauge
            chartData={40}
            style={{ height: '60px', width: '60px' }}
            containerSize={{ height: 190, width: 190 }}
            layoutEdit={[
                {
                    path: ['series', 0, 'pointer', 'width'],
                    value: 15,
                },
                {
                    path: ['series', 0, 'axisTick', 'distance'],
                    value: -15,
                },
                {
                    path: ['series', 0, 'detail', 'rich', 'a', 'fontSize'],
                    value: 25,
                },
                {
                    path: ['series', 0, 'detail', 'rich', 'a', 'lineHeight'],
                    value: 25,
                },
                {
                    path: ['series', 0, 'detail', 'offsetCenter'],
                    value: [0, '35%'],
                },
                {
                    path: ['series', 0, 'backgroundColor'],
                    value: [0, '#fff'],
                },
            ]}
        />,
        key: 'gauge0-5',
        defaultSize: 2,
    },
    {
        chart: <MultiText
            chartData={[
                <div style={{ fontSize: '8px', textAlign: 'center' }}>Data One</div>,
                <div style={{ fontSize: '8px', textAlign: 'center' }}>Data Two</div>,
                <div style={{ fontSize: '8px', textAlign: 'center' }}>Data Three</div>,
                <div style={{ fontSize: '8px', textAlign: 'center' }}>Data Four</div>,
            ]}
            style={{ height: '60px', width: '60px' }}
            containerSize={{ height: 190, width: 190 }}
        />,
        key: 'multiText',
        defaultSize: 2,
    },
    {
        chart: (
            <div className="quadrantAnalysisContainer">
                <div className="quadrantAnalysis" style={{ width: '60px' }}>
                    <div className="tl" style={{ width: '29px', height: '16px' }}><div className="ss" /></div>
                    <div className="tr" style={{ width: '29px', height: '16px' }}><div className="ss" /></div>
                    <div className="bl bottom" style={{ width: '29px', height: '16px' }}><div className="ss" /></div>
                    <div className="br bottom" style={{ width: '29px', height: '16px' }}><div className="ss" /></div>
                </div>
            </div>
        ),
        key: 'quadrantAnalysis',
        defaultSize: 8,
    },
    {
        chart: (
            <LMX
                chartData={[
                    { name: '', value: '' },
                    { name: '', value: '' },
                    { name: '', value: '' },
                    { name: '', value: '' },
                ]}
                style={{ height: '40px', width: '60px' }}
            />
        ),
        key: 'lmx',
        defaultSize: 8,
    },
]

@observer
class SelectTemplateModal extends Component {
    constructor(props) {
        super(props)

        const { dashStore } = props
        const { productMap } = dashStore
        const productTypes = getProductTypes(productMap)
        const surveyTypes = getSurveyTypes(productMap)
        const filters = productTypes.includes('CE3P') && surveyTypes.includes('EM')
            ? [...productTypes, 'EM'] : productTypes

        this.state = {
            selectedTemplate: undefined,
            filters,
        }
    }

    templateSettingsFilter = (dashboard) => {
        const { dashStore } = this.props
        const { productMap } = dashStore
        const industries = getIndustries(productMap)
        const productTypes = getProductTypes(productMap)
        const surveyTypes = getSurveyTypes(productMap)
        const templateSettings = R.pathOr({}, ['widgets', 'templateSettings'], dashboard)
        return industries.includes(templateSettings.industry)
            && productTypes.includes(templateSettings.productType)
            && surveyTypes.includes(templateSettings.surveyType)
    }

    getTemplateOptions = () => {
        const { dashStore } = this.props
        const { dashboards, oId } = dashStore
        const { selectedTemplate, filters } = this.state
        const icons = chartIcons.reduce((obj, ci) => ({ ...obj, [ci.key]: ci }), {})

        const templateOptions = dashboards
            .filter((d) => (
                d.isTemplate && oId === d.oId
                && R.pathOr(false, ['widgets', 'templateSettings', 'setAsTemplate'], d)
                && (filters.includes(R.pathOr(false, ['widgets', 'templateSettings', 'productType'], d))
                || (R.pathOr(false, ['widgets', 'templateSettings', 'productType'], d) === 'CE3P'
                && R.pathOr(false, ['widgets', 'templateSettings', 'surveyType'], d) === 'EM'
                && filters.includes('EM'))
                )
            && this.templateSettingsFilter(d)))

        if (!templateOptions.length) {
            return (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <img style={{ width: '150px' }} src={`${process.env.CDN_URL}/images/search-icon-v2.svg`} />
                        <h3 style={{ fontWeight: 'bold', color: '#8B99A7' }}>{i18n.t('sw_templatetextnoresults')}</h3>
                    </div>
                </div>
            )
        }
        return templateOptions.map((to) => {
            const { widgets } = to
            const noMetrics = R.pathOr([], ['templateSettings', 'metrics'], widgets).length
            const imgWidgets = R.pathOr([], ['widgets'], widgets).slice(0, 4)
            return (
                <div
                    className={`templateOption ${selectedTemplate === to.dashboardId ? 'active' : ''}`}
                    onClick={() => this.setState({ selectedTemplate: to.dashboardId })}
                >
                    <div className="templateImage">
                        {imgWidgets.map((w) => icons[w.chartType]?.chart)}
                    </div>
                    <div className="metricContainer">
                        <hr />
                        <div className="metrics">
                            <i className="fa-solid fa-square-poll-vertical" />
                            {`${noMetrics} Instant metric${noMetrics === 1 ? '' : 's'}`}
                            <i className="fa-solid fa-circle-question">
                                <Tooltip
                                    text={(
                                        <div className="metricContent">
                                            {R.pathOr([], ['templateSettings', 'metrics'], widgets).map((m) => (
                                                <div>
                                                    <i className="fa-solid fa-chart-mixed" />
                                                    {m}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    direction="up"
                                />
                            </i>
                        </div>
                    </div>
                    <div className="title">{to.label}</div>
                    <div className="description">{R.pathOr('', ['templateSettings', 'description'], widgets)}</div>
                    <div className="checkboxContainer">
                        <div>
                            <Checkbox
                                checked={selectedTemplate === to.dashboardId}
                            />
                        </div>
                    </div>
                </div>
            )
        })
    }

    close = () => {
        const { dashStore } = this.props
        const { toggleShowTemplateSelection } = dashStore
        this.setState({ selectedTemplate: undefined })
        toggleShowTemplateSelection()
    }

    save = () => {
        const { dashStore } = this.props
        const { cloneDashboard, toggleShowTemplateSelection } = dashStore
        const { selectedTemplate } = this.state
        cloneDashboard(selectedTemplate)
        toggleShowTemplateSelection()
    }

    changeFilters = ({ value }) => {
        this.setState({ filters: value.map((filter) => filter.value) })
    }

    renderFilters = () => {
        const { dashStore } = this.props
        const { productMap } = dashStore
        const productTypes = getProductTypes(productMap, true)
        const surveyTypes = getSurveyTypes(productMap, true)
        const options = productTypes.some((p) => p.value === 'CE3P')
        && surveyTypes.some((p) => p.value === 'EM')
            ? [...productTypes, surveyTypes.find((p) => p.value === 'EM')] : productTypes

        return (
            <TreeviewSelect
                options={options.map((option) => (
                    { ...option, checked: true, children: undefined }
                ))}
                onChange={this.changeFilters}
                returnAllSelectedNodes
                label="Filter:"
                numSelected={options.length}
            />
        )
    }

    render() {
        const { dashStore } = this.props
        const { showTemplateSelection } = dashStore
        const { selectedTemplate } = this.state
        return (
            <Modal
                show={showTemplateSelection}
                onClose={this.close}
                cssClass="templateSelectionModal"
            >
                <ModalHeader cssClass="chartAdvancedPreview-header ">
                    <div className="title">
                        {i18n.t('dashboards.template.title')}
                    </div>
                    <div className="subTitle">
                        {i18n.t('dashboards.template.subTitle')}
                    </div>
                </ModalHeader>
                <div style={{
                    textAlign: 'left', width: '190px', position: 'absolute', top: '30px',
                }}
                >
                    {this.renderFilters()}
                </div>
                <div className="body">
                    <Carousel
                        items={this.getTemplateOptions()}
                        scrollButtonWidth={780}
                    />
                </div>
                <ModalFooter>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                        <Button
                            label={(
                                <div>
                                    {i18n.t('cancel')}
                                </div>
                            )}
                            cssClass="tertiary regular"
                            onClick={this.close}
                        />
                        <Button
                            label={(
                                <div>
                                    {i18n.t('save')}
                                </div>
                            )}
                            cssClass="primary regular"
                            style={{ width: '200px', fontWeight: 600, textAlign: 'center' }}
                            onClick={this.save}
                            disabled={!selectedTemplate}
                        />
                    </div>
                </ModalFooter>
            </Modal>
        )
    }
}

export default SelectTemplateModal
